import { ComponentProps } from "react";
import IconButton from "components/IconButton";
import { Popover } from "components/Popover/Popover";
import "./TableOptionMenu.scss";

export type TableOption<T extends string> = { value: T; label: string };

type TableOptionMenuProps<T extends string> = {
    /** Icon of the toolbar trigger. */
    icon: ComponentProps<typeof IconButton>["icon"];
    /** Accessible name of the trigger and the option group, e.g. "Row density". */
    label: string;
    value: T;
    options: TableOption<T>[];
    onChange: (value: T) => void;
    /** Extra class on the wrapper, for caller-specific tweaks. */
    className?: string;
};

/**
 * Toolbar control (one icon that opens a small menu) to pick one value of a
 * table view setting: row density, row grouping. The menu looks like the app's
 * other popover menus (`ul.popoverMenu`): left-aligned rows, light-grey hover,
 * the selected row marked with a grey background. Presentational only; the
 * caller owns and persists the value.
 */
function TableOptionMenu<T extends string>({
    icon,
    label,
    value,
    options,
    onChange,
    className,
}: TableOptionMenuProps<T>) {
    const active = options.find((opt) => opt.value === value);
    return (
        <div
            className={`tableOptionMenu${className ? ` ${className}` : ""}`}
            title={active ? `${label}: ${active.label}` : label}
        >
            <Popover
                trigger={<IconButton icon={icon} aria-label={label} />}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                contentClassName="table-option-menu__popover"
            >
                <ul className="popoverMenu table-option-menu" role="menu" aria-label={label}>
                    {options.map((opt) => {
                        const selected = opt.value === value;
                        return (
                            <li key={opt.value}>
                                <button
                                    type="button"
                                    role="menuitemradio"
                                    aria-checked={selected}
                                    className={selected ? "selected" : undefined}
                                    onClick={() => onChange(opt.value)}
                                >
                                    {opt.label}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </Popover>
        </div>
    );
}

export default TableOptionMenu;
